/**
 * UserSwitcher — platform-ops only. Allows "view as" another user in the
 * current tenant. Sets/clears the apex_assume_user cookie; the server
 * middleware swaps the request identity to the target (view-only).
 */

import { useState, useRef, useEffect } from 'react';
import { Shield, ChevronDown, Check, X, Search } from 'lucide-react';
import { useAuth } from '../../providers/AuthProvider';
import { apiGet } from '../../hooks/useApi';

interface ImpersonationTarget {
  id: string;
  email: string;
  name: string;
  role: string;
}

const IMPERSONATION_COOKIE = 'apex_assume_user';

function setCookie(name: string, value: string, days = 1) {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${value};expires=${expires};path=/`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

export function UserSwitcher() {
  const { user, impersonationTarget } = useAuth();
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<ImpersonationTarget[] | null>(null);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  // Load the target list lazily on first open.
  useEffect(() => {
    if (!open || targets !== null) return;
    apiGet<{ targets: ImpersonationTarget[] }>('/auth/impersonation-targets')
      .then((data) => setTargets(data.targets.filter((t) => t.id !== user?.sub)))
      .catch(() => setTargets([]));
  }, [open, targets, user?.sub]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Only platform-ops can use this
  if (!user?.platformOps) return null;

  const handleSelect = (target: ImpersonationTarget) => {
    setCookie(IMPERSONATION_COOKIE, target.id);
    setOpen(false);
    // Reload to apply the new effective identity across the app
    window.location.reload();
  };

  const handleClear = () => {
    deleteCookie(IMPERSONATION_COOKIE);
    setOpen(false);
    window.location.reload();
  };

  const q = query.trim().toLowerCase();
  const filtered = (targets ?? []).filter(
    (t) => !q || t.name.toLowerCase().includes(q) || t.email.toLowerCase().includes(q),
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[0.6875rem] font-medium border transition-colors cursor-pointer ${
          impersonationTarget
            ? 'bg-amber-500/10 border-amber-500/20 text-amber-600'
            : 'bg-secondary/30 border-border/60 text-muted-foreground hover:text-foreground'
        }`}
      >
        <Shield className="w-3 h-3 stroke-[1.5]" />
        {impersonationTarget ? `Viewing as: ${impersonationTarget.name}` : 'Impersonate'}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1.5 w-[260px] bg-card border border-border/80 rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] z-50 overflow-hidden">
          <div className="py-1">
            <div className="px-3 py-1.5 text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider">
              View As User
            </div>
            <div className="px-3 pb-1.5">
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-border/60 bg-secondary/30">
                <Search className="w-3 h-3 text-muted-foreground stroke-[1.5]" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name or email"
                  className="w-full bg-transparent text-[0.75rem] outline-none placeholder:text-muted-foreground"
                />
              </div>
            </div>
            <div className="max-h-[260px] overflow-y-auto">
              {targets === null ? (
                <div className="px-3 py-2 text-[0.75rem] text-muted-foreground">Loading…</div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-2 text-[0.75rem] text-muted-foreground">No users found</div>
              ) : (
                filtered.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handleSelect(t)}
                    className={`w-full flex items-center justify-between px-3 py-1.5 text-[0.75rem] hover:bg-secondary transition-colors cursor-pointer ${
                      impersonationTarget?.id === t.id
                        ? 'bg-amber-500/5 font-medium text-amber-600'
                        : ''
                    }`}
                  >
                    <span className="flex flex-col items-start min-w-0">
                      <span className="truncate max-w-[180px]">{t.name}</span>
                      <span className="text-[0.625rem] text-muted-foreground truncate max-w-[180px]">
                        {t.email} · {t.role}
                      </span>
                    </span>
                    {impersonationTarget?.id === t.id && (
                      <Check className="w-3 h-3 text-amber-600 stroke-[2] shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>

            {impersonationTarget && (
              <>
                <div className="h-px bg-border/40 my-1" />
                <button
                  onClick={handleClear}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[0.75rem] text-destructive hover:bg-destructive/5 transition-colors cursor-pointer"
                >
                  <X className="w-3 h-3 stroke-[2]" /> Stop impersonating
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
